<script lang="ts">
  /**
   * Settings: alerts, push, passkeys (brief §14).
   *
   * The paragraph about background alerts is not filler. A PWA cannot run a geofence while it
   * is closed — there is no web API for it that works — and somebody who turns alerts on and
   * then never hears from the app again will conclude it is broken rather than that the
   * platform cannot do it. So the page says so, and offers the APK.
   */
  import {
    DAILY_CAP_RANGE,
    type AlertSettings,
    THRESHOLD_RANGE,
  } from '$domain/geofence.ts';
  import { regionOf } from '$domain/h3.ts';
  import { cellFor } from '$domain/h3.ts';
  import type { UserSettings } from '$domain/settings.ts';
  import { resolutionOf } from '$domain/h3.ts';
  import { normaliseSettings } from '$domain/settings.ts';
  import { request } from './api.ts';
  import { gdiColour } from './gdi-scale.ts';
  import { outbox } from './outbox.svelte.ts';
  import {
    askLocalNotificationPermission,
    disablePush,
    enablePush,
    type PushState,
    pushState,
  } from './push.ts';
  import InstallApp from './InstallApp.svelte';
  import { describeError, session } from './session.svelte.ts';

  type Props = {
    settings: UserSettings;
    /** From `/api/config`; null when this instance has no such build to hand out. */
    apk: { version: string; size: number } | null;
    wearApk: { version: string; size: number } | null;
    onclose: () => void;
    onsaved: (settings: UserSettings) => void;
  };

  const { settings, apk, wearApk, onclose, onsaved }: Props = $props();

  // svelte-ignore state_referenced_locally
  let draft = $state<UserSettings>(structuredClone($state.snapshot(settings)));
  let busy = $state(false);
  let error = $state('');
  let push = $state<PushState>('off');
  let localPermission = $state<NotificationPermission>('default');
  let confirmingDelete = $state(false);

  const quietOff = $derived(draft.alerts.quietFrom === null || draft.alerts.quietTo === null);

  $effect(() => {
    void (async () => {
      push = await pushState().catch(() => 'unsupported' as PushState);
      if (typeof Notification !== 'undefined') localPermission = Notification.permission;
    })();
  });

  function patchAlerts(patch: Partial<AlertSettings>) {
    draft = { ...draft, alerts: { ...draft.alerts, ...patch } };
  }

  async function save() {
    busy = true;
    error = '';
    try {
      // Clamped here with the **same function the server uses** — which is the whole reason
      // `src/domain` is importable from the browser. What the sheet shows after saving is
      // what will be stored, even though the write has not left the device yet.
      const settled = normaliseSettings($state.snapshot(draft), resolutionOf);
      await outbox.enqueue('settings', { settings: settled });
      draft = settled;
      onsaved(settled);
    } catch (cause) {
      error = describeError(cause);
    } finally {
      busy = false;
    }
  }

  async function togglePush() {
    busy = true;
    error = '';
    try {
      push = push === 'on' ? await disablePush() : await enablePush();
    } catch (cause) {
      error = describeError(cause);
    } finally {
      busy = false;
    }
  }

  async function setHomeFromPosition() {
    error = '';
    navigator.geolocation.getCurrentPosition(
      (fix) => {
        // Rounded to res 5 here, on the device. What the server stores is a 250 km² region —
        // a conurbation, not a neighbourhood (brief §16).
        const region = regionOf(cellFor(fix.coords.latitude, fix.coords.longitude));
        draft = { ...draft, homeCell5: region };
      },
      () => {
        error = 'Ohne Standortfreigabe geht das nicht.';
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 600_000 },
    );
  }

  /**
   * The export, handed over as a file (brief §18).
   *
   * Fetched and turned into a blob rather than linked directly, because the endpoint needs the
   * session cookie and an `<a download>` to it would work in a browser and silently not in
   * the APK's WebView.
   */
  async function exportData() {
    busy = true;
    error = '';
    try {
      const data = await request<unknown>('/api/me/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hotspotter-${session.user?.username ?? 'export'}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (cause) {
      error = describeError(cause);
    } finally {
      busy = false;
    }
  }

  async function leave(mode: 'anonymize' | 'purge') {
    busy = true;
    error = '';
    try {
      await request(`/api/me?mode=${mode}`, { method: 'DELETE' });
      await session.signOut();
      location.reload();
    } catch (cause) {
      error = describeError(cause);
    } finally {
      busy = false;
    }
  }

  const PUSH_LABELS: Record<PushState, string> = {
    unsupported: 'Dieser Browser kann keine Push-Nachrichten.',
    unavailable: 'Auf dieser Instanz ist Push nicht eingerichtet.',
    denied: 'Benachrichtigungen sind im Browser blockiert.',
    off: 'Aus',
    on: 'An',
  };
</script>

<div class="backdrop" role="presentation" onclick={onclose}></div>

<section class="sheet" aria-label="Einstellungen">
  <header>
    <h2>Einstellungen</h2>
    <button class="close" onclick={onclose} aria-label="Schließen">×</button>
  </header>

  <h3>Warnungen</h3>

  <label class="row">
    <span>Warnen, wenn es irgendwo losgeht</span>
    <input
      type="checkbox"
      checked={draft.alerts.enabled}
      onchange={(event) => patchAlerts({ enabled: event.currentTarget.checked })}
    />
  </label>

  <div class="field">
    <span class="label">
      Ab GDI
      <b style={`color: ${gdiColour(draft.alerts.threshold)}`}>{draft.alerts.threshold}</b>
    </span>
    <input
      type="range"
      min={THRESHOLD_RANGE.min}
      max={THRESHOLD_RANGE.max}
      step="5"
      value={draft.alerts.threshold}
      oninput={(event) => patchAlerts({ threshold: Number(event.currentTarget.value) })}
    />
  </div>

  <div class="field">
    <span class="label">Höchstens {draft.alerts.dailyCap} Warnungen pro Tag</span>
    <input
      type="range"
      min={DAILY_CAP_RANGE.min}
      max={DAILY_CAP_RANGE.max}
      step="1"
      value={draft.alerts.dailyCap}
      oninput={(event) => patchAlerts({ dailyCap: Number(event.currentTarget.value) })}
    />
  </div>

  <label class="row">
    <span>Ruhezeit</span>
    <input
      type="checkbox"
      checked={!quietOff}
      onchange={(event) =>
        patchAlerts(
          event.currentTarget.checked ? { quietFrom: 22, quietTo: 8 } : { quietFrom: null, quietTo: null },
        )}
    />
  </label>

  {#if !quietOff}
    <div class="row">
      <span>von</span>
      <input
        type="number"
        min="0"
        max="23"
        value={draft.alerts.quietFrom}
        oninput={(event) => patchAlerts({ quietFrom: Number(event.currentTarget.value) })}
        aria-label="Ruhezeit von"
      />
      <span>bis</span>
      <input
        type="number"
        min="0"
        max="23"
        value={draft.alerts.quietTo}
        oninput={(event) => patchAlerts({ quietTo: Number(event.currentTarget.value) })}
        aria-label="Ruhezeit bis"
      />
    </div>
  {/if}

  {#if localPermission !== 'granted'}
    <button
      class="secondary"
      onclick={async () => {
        localPermission = await askLocalNotificationPermission();
      }}
    >
      Benachrichtigungen erlauben
    </button>
  {/if}

  <p class="note">
    Im Browser laufen Warnungen nur, solange die App offen ist — für Hintergrund-Warnungen gibt
    es keine brauchbare Web-Schnittstelle. Die Android-App kann das.
  </p>

  <InstallApp {apk} {wearApk} />

  <h3>Gerät koppeln</h3>
  <p class="note">
    Eine Uhr oder ein Telefon zeigt beim ersten Start einen Code. Der wird hier freigegeben —
    die Uhr kann ihn nicht selbst übergeben, sie kann ihn nur anzeigen.
  </p>
  <a class="pair" href="/pair">Code eingeben</a>

  <h3>Meldungen aus deiner Gegend</h3>
  <div class="row">
    <span>{draft.homeCell5 === null ? 'Keine Heimatregion gesetzt' : 'Heimatregion gesetzt'}</span>
    <button class="secondary" onclick={setHomeFromPosition}>Hier</button>
    {#if draft.homeCell5 !== null}
      <button class="secondary" onclick={() => (draft = { ...draft, homeCell5: null })}>
        Löschen
      </button>
    {/if}
  </div>
  <p class="note">
    Gespeichert wird eine Wabe von rund 250 km² — der Großraum, nicht dein Viertel.
  </p>

  <label class="row">
    <span>Push-Benachrichtigungen: {PUSH_LABELS[push]}</span>
    <button
      class="secondary"
      disabled={busy || push === 'unsupported' || push === 'unavailable' || push === 'denied'}
      onclick={togglePush}
    >
      {push === 'on' ? 'Aus' : 'An'}
    </button>
  </label>

  {#if push === 'on'}
    <button
      class="secondary"
      onclick={() => request('/api/push/test', { method: 'POST', body: '{}' })}
    >
      Test-Benachrichtigung schicken
    </button>
  {/if}

  <h3>Neue Meldungen sind standardmäßig</h3>
  <div class="visibility">
    {#each [{ value: 2, label: 'für alle' }, { value: 0, label: 'nur für mich' }] as option (option.value)}
      <button
        class:selected={draft.defaultVisibility === option.value}
        onclick={() =>
          (draft = { ...draft, defaultVisibility: option.value as 0 | 1 | 2 })}
      >
        {option.label}
      </button>
    {/each}
  </div>

  <h3>Passkeys</h3>
  <ul class="passkeys">
    {#each session.credentials as credential (credential.id)}
      <li>
        <span>{credential.authenticator}</span>
        <span class="when">
          {credential.lastUsedAt
            ? `zuletzt ${new Date(credential.lastUsedAt).toLocaleDateString('de-DE')}`
            : 'noch nicht benutzt'}
        </span>
      </li>
    {/each}
  </ul>

  {#if error}<p class="error" role="alert">{error}</p>{/if}

  <button class="primary" disabled={busy} onclick={save}>
    {busy ? 'Speichere …' : 'Speichern'}
  </button>
  <button class="secondary" onclick={() => session.signOut()}>Abmelden</button>

  <h3>Deine Daten</h3>
  <p class="note">
    Der Export enthält alles, was zum Konto gehört. Positionen sind nicht dabei — es gibt keine:
    gespeichert wird nur die Wabe, in der etwas gemeldet wurde.
  </p>
  <button class="secondary" onclick={exportData}>Daten exportieren</button>

  {#if confirmingDelete}
    <p class="note">
      Beim Anonymisieren bleiben deine Meldungen auf der Karte, aber ohne dich — alles
      Persönliche ist weg. Beim vollständigen Löschen geht auch die Karten-Historie mit.
      Beides ist sofort und endgültig.
    </p>
    <button class="secondary" disabled={busy} onclick={() => leave('anonymize')}>
      Konto löschen, Meldungen anonymisiert behalten
    </button>
    <button class="danger" disabled={busy} onclick={() => leave('purge')}>
      Alles restlos löschen
    </button>
    <button class="secondary" onclick={() => (confirmingDelete = false)}>Abbrechen</button>
  {:else}
    <button class="secondary" onclick={() => (confirmingDelete = true)}>Konto löschen</button>
  {/if}
</section>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgb(0 0 0 / 40%);
    z-index: 5;
  }

  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 6;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    max-height: 88vh;
    overflow-y: auto;
    padding: 1rem 1rem calc(1rem + env(safe-area-inset-bottom));
    border-radius: 1.25rem 1.25rem 0 0;
    background: var(--card);
    box-shadow: 0 -8px 32px rgb(0 0 0 / 25%);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  h2 {
    margin: 0;
    font-size: 1.1rem;
  }

  h3 {
    margin: 0.5rem 0 0;
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .close {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 1.75rem;
    line-height: 1;
    cursor: pointer;
  }

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    font-size: 0.9375rem;
  }

  .field {
    display: grid;
    gap: 0.2rem;
  }

  .label {
    font-size: 0.9375rem;
  }

  input[type='range'] {
    width: 100%;
    accent-color: var(--accent);
  }

  input[type='number'] {
    width: 4rem;
    padding: 0.35rem 0.5rem;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    background: var(--bg);
    color: var(--fg);
  }

  .pair {
    display: block;
    padding: 0.6rem 1rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
    color: var(--fg);
    font-size: 0.875rem;
    text-align: center;
    text-decoration: none;
  }

  .note {
    margin: 0;
    font-size: 0.75rem;
    color: var(--muted);
  }

  .visibility {
    display: flex;
    gap: 0.5rem;
  }

  .visibility button {
    flex: 1;
    padding: 0.5rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
    background: var(--bg);
    color: var(--fg);
    cursor: pointer;
  }

  .visibility button.selected {
    border-color: var(--accent);
    background: color-mix(in srgb, var(--accent) 15%, transparent);
  }

  .passkeys {
    display: grid;
    gap: 0.2rem;
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: 0.8125rem;
  }

  .passkeys li {
    display: flex;
    justify-content: space-between;
  }

  .when {
    color: var(--muted);
  }

  .primary,
  .secondary {
    padding: 0.75rem;
    border-radius: 0.75rem;
    font-size: 0.9375rem;
    cursor: pointer;
  }

  .primary {
    border: 0;
    background: var(--accent);
    color: #fff;
    font-weight: 600;
  }

  .secondary {
    border: 1px solid var(--border);
    background: var(--bg);
    color: var(--fg);
  }

  .danger {
    padding: 0.75rem;
    border: 1px solid var(--accent);
    border-radius: 0.75rem;
    background: none;
    color: var(--accent);
    font-size: 0.9375rem;
    cursor: pointer;
  }

  .primary:disabled,
  .secondary:disabled {
    opacity: 0.5;
    cursor: default;
  }

  .error {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--accent);
  }
</style>
